import { Component, OnInit, ViewEncapsulation, SimpleChanges, OnChanges, Input } from '@angular/core';

import { Observable } from 'rxjs';

import { Vraag, Antwoord, ValidatieParameter, SliderParameters } from '@models/claims';
import { VraagboomMultiResultaat, Verzekering, Meetgegevens, VraagboomNumberResultaat, VraagboomTextResultaat, VraagboomAntwoord, IBaseVraagboomResultaat } from '@models/vragenboom';
import { BaseVraagControlDirective } from '../base/base-vraag-control';
import { ValidatieregelHelper } from '@services/public_api';
import { MetenMapper } from '../../../mappers/meten.mapper';
import { DataService } from '../../../services/data.service';
import { NumberValidator } from '@components/lib/utils/validation/number.validator';
import { TextValidator } from '@components/lib/utils/validation/text.validator';
import { DoorvraagType } from '@components/lib/models/generic/doorvraagtype.enum';
import { SliderHelper } from '@services/lib/helpers/slider-helper';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-multiselect-doorvragen',
  templateUrl: './multiselect-doorvragen.component.html',
  styleUrls: ['./multiselect-doorvragen.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MultiselectDoorvragenComponent extends BaseVraagControlDirective<VraagboomMultiResultaat> implements OnInit, OnChanges {
  private antwoordenDictionary: Map<Antwoord, Antwoord[]> = new Map<Antwoord, Antwoord[]>();

  minChoices = 0;
  private maxChoices = 0;

  selectedAntwoordIds: string[] = [];
  private sliderValues: Map<string, number> = new Map<string, number>();
  private textAreaValues: Map<string, string> = new Map<string, string>();

  private verzekering: Verzekering;
  private meetgegevens: Meetgegevens;

  private sliderParametersMap = new Map<string, SliderParameters>();

  hoofdAntwoorden: Antwoord[];
  doorvraagType = DoorvraagType;
  resultaten$: Observable<VraagboomAntwoord<IBaseVraagboomResultaat>[]>;
  resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[];

  @Input() isCurrentVraag: boolean;

  constructor(_dataService: DataService) {
    super(_dataService);
  }

  ngOnInit() {
    this.verzekering = this._dataService.getVerzekering();
    this.meetgegevens = this._dataService.getMeetgegevens();

    if (!this.vraag)
      return;

    this.initializeMinMax();
    this.initializeAntwoordenDictionary();

    this.fillPreviousAnswers();
  }

  initialize(changes: SimpleChanges): void {
    // resultaten ophalen tbv validatie
    this.resultaten$ = this._dataService.resultaten;
    this.resultaten$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(res => {
        if (res !== undefined && res !== null) {
          this.resultaten = res;
        }
      });
  }

  private initializeMinMax() {
    const minMaxNumberValidator: ValidatieParameter = ValidatieregelHelper.get(this.vraag.validatieParameters, 'MIN_MAX');
    if (minMaxNumberValidator == null)
      return;

    const params = minMaxNumberValidator.validatie_parameters.split(';');
    if ((params?.length ?? 0) < 2)
      return;

    this.minChoices = Number(params[0]);
    this.maxChoices = Number(params[1]);
  }

  private initializeAntwoordenDictionary() {
    if ((this.vraag?.antwoorden?.length ?? 0) == 0)
      return;

    this.vraag.antwoorden.map(x => x.groep_id).filter(this.onlyUnique).forEach(groepId => {
      this.vraag.antwoorden.filter(x => x.groep_id === groepId).forEach((antwoord, index, antwoorden) => {
        if (index == 0)
          this.antwoordenDictionary.set(antwoord, []);
        else {
          this.antwoordenDictionary.get(antwoorden[0]).push(antwoord);
        }
      })
    });

    this.hoofdAntwoorden = Array.from(this.antwoordenDictionary.keys());
  }

  private onlyUnique(value, index, self) {
    return self.indexOf(value) === index;
  }

  private fillPreviousAnswers() {
    if ((this.vraag?.antwoorden?.length ?? 0) == 0 || this.antwoordResultaat?.resultaat == null)
      return;

    if (this.antwoordResultaat.antwoorden != null)
      this.selectedAntwoordIds = this.antwoordResultaat.antwoorden.map(a => a.id);

    this.antwoordResultaat?.resultaat.resultaten.forEach((value, key) => {
      const antwoord = this.vraag.antwoorden.find(a => a.id == key);
      if (antwoord == null)
        return;

      switch (antwoord.sub_control_type) {
        case DoorvraagType.Slider:
          this.sliderValues.set(antwoord.id, +(value as VraagboomNumberResultaat).value);
          break;

        case DoorvraagType.TextArea:
          this.textAreaValues.set(antwoord.id, (value as VraagboomTextResultaat).value);
          break;
      }
    });
  }

  getSubAntwoorden(hoofdAntwoord: Antwoord): Antwoord[] {
    if (!this.antwoordenDictionary)
      return [];

    return this.antwoordenDictionary.get(hoofdAntwoord);
  }

  getSubAntwoordenLength(hoofdAntwoord: Antwoord): number {
    return this.getSubAntwoorden(hoofdAntwoord).length;
  }

  isAntwoordSelected(antwoord: Antwoord): boolean {
    return antwoord && this.selectedAntwoordIds?.indexOf(antwoord.id) >= 0;
  }

  getSliderValue(antwoord: Antwoord): number {
    if (!this.sliderValues.has(antwoord.id))
      this.sliderValues.set(antwoord.id, SliderHelper.getDefaultValue(this.getSliderParameters(antwoord)));

    return this.sliderValues.get(antwoord.id);
  }

  getTextAreaValue(antwoord: Antwoord): string {
    if (!this.textAreaValues.has(antwoord.id))
      this.textAreaValues.set(antwoord.id, '');

    return this.textAreaValues.get(antwoord.id);
  }

  checkboxClicked(antwoord: Antwoord) {
    if (!this.isCurrentVraag)
      return;

    if (this.selectedAntwoordIds?.indexOf(antwoord.id) >= 0) {
      this.selectedAntwoordIds = this.selectedAntwoordIds.filter(id => id !== antwoord.id);
    } else {
      if (this.selectedAntwoordIds.length >= this.maxChoices)
        return;

      this.selectedAntwoordIds.push(antwoord.id);
    }

    this.notifyAntwoord();
  }

  sliderValueChanged(antwoord: Antwoord, value: number) {
    this.sliderValues.set(antwoord.id, value);
    this.notifyAntwoord();
  }

  textAreaValueChanged(antwoord: Antwoord, value: string) {
    this.textAreaValues.set(antwoord.id, value);
    this.notifyAntwoord();
  }

  getValidatieParameter(antwoord: Antwoord, key: string, defaultValue: string): string {
    return antwoord.validatieParameters.find(vp => vp.type_validatie === key)?.validatie_parameters ?? defaultValue;
  }

  geefMeetid(vraagtitel: string, antwoordtitel: string): string {
    if (this.meetgegevens == null) {
      return '';
    }

    return 'ipoclick-checkbox-' + this._dataService.getMeetidPazaProces() + '-' +
      this.meetgegevens.status + this.meetgegevens.sleutel +
      MetenMapper.geefVolgnr(this.vraag, this.meetgegevens.reeks) + '_' +
      MetenMapper.format(this.verzekering.sleutel) + '-' +
      MetenMapper.format(vraagtitel) + '-' + MetenMapper.format(antwoordtitel);
  }

  notifyAntwoord() {
    const antwoorden: Antwoord[] = [];

    if (this.vraag && this.vraag.antwoorden) {
      for (const id of this.selectedAntwoordIds) {
        const antwoord = this.vraag.antwoorden.find(a => a.id === id);
        antwoorden.push(antwoord);
      }
    }

    const resultaat = new VraagboomMultiResultaat();
    this.sliderValues.forEach((sv, sk) => {
      const subResultaat: VraagboomNumberResultaat = new VraagboomNumberResultaat();
      subResultaat.value = sv.toString();
      resultaat.resultaten.set(sk, subResultaat);
    });

    this.textAreaValues.forEach((tv, tk) => {
      resultaat.resultaten.set(tk, new VraagboomTextResultaat(tv));
    });

    this.setAntwoord(antwoorden, resultaat);
  }

  validate(vraag: Vraag, antwoorden: Antwoord[], resultaat: VraagboomMultiResultaat): boolean {
    return antwoorden &&
      antwoorden.length >= this.minChoices &&
      (this.maxChoices == 0 || antwoorden.length <= this.maxChoices) &&
      this.validateSubQuestions(antwoorden, resultaat);
  }

  validateSubQuestions(antwoorden: Antwoord[], resultaat: VraagboomMultiResultaat): boolean {
    return antwoorden.every(antwoord => {
      return this.antwoordenDictionary.get(antwoord).every(subAntwoord => {

        if (!resultaat.resultaten.has(subAntwoord.id))
          return false;

        if (subAntwoord.validatieParameters?.length == 0 ?? true)
          return true;

        const antwoordResultaat = resultaat.resultaten.get(subAntwoord.id);

        if (antwoordResultaat instanceof VraagboomNumberResultaat)
          return NumberValidator.isValidNumber(this.vraag, antwoordResultaat, this.resultaten);

        if (antwoordResultaat instanceof VraagboomTextResultaat)
          return antwoordResultaat.value.length > 0 ? TextValidator.isValidMaxLength([subAntwoord], antwoordResultaat) : false;

      })
    });
  }

  getSliderParameters(antwoord: Antwoord): SliderParameters {
    if (!this.sliderParametersMap.has(antwoord.id))
      this.sliderParametersMap.set(antwoord.id, SliderHelper.getSliderParameters(antwoord.controlParameters));

    return this.sliderParametersMap.get(antwoord.id);
  }
}
